"use client";

import { useEffect, useState } from "react";
import {
  calculateFunding,
  initialFundingDraft,
  type FundingDraft,
} from "@/lib/calculators/acquisition-funding";

const storageKey = "venturu-acquisition-scenario:v1";
export const fundingCurrencies = ["USD", "CAD", "GBP", "EUR", "AUD", "NZD"];

/** A tab-local scenario shared by the guide and the dedicated calculator. */
export function useFundingScenario() {
  const [draft, setDraft] = useState({ ...initialFundingDraft });
  const [currency, setCurrency] = useState("USD");
  const [sellerEnabled, setSellerEnabled] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    try {
      const stored: unknown = JSON.parse(
        sessionStorage.getItem(storageKey) ?? "null",
      );
      if (
        stored &&
        typeof stored === "object" &&
        "version" in stored &&
        stored.version === 1 &&
        "draft" in stored &&
        stored.draft &&
        typeof stored.draft === "object" &&
        "currency" in stored &&
        typeof stored.currency === "string" &&
        fundingCurrencies.includes(stored.currency) &&
        "sellerEnabled" in stored &&
        typeof stored.sellerEnabled === "boolean"
      ) {
        const values = stored.draft as Record<string, unknown>;
        if (
          Object.entries(initialFundingDraft).every(
            ([key, value]) => typeof values[key] === typeof value,
          )
        ) {
          const candidate = values as FundingDraft;
          if (
            calculateFunding(candidate).ok &&
            (stored.sellerEnabled || candidate.sellerAmount === "0")
          ) {
            setDraft(candidate);
            setCurrency(stored.currency);
            setSellerEnabled(stored.sellerEnabled);
          }
        }
      }
    } catch {
      /* Storage can be unavailable in private browsing. Calculation still works. */
    }
    setReady(true);
  }, []);
  useEffect(() => {
    if (!ready) return;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ version: 1, draft, currency, sellerEnabled }),
      );
    } catch {
      /* Persistence is optional. */
    }
  }, [draft, currency, sellerEnabled, ready]);
  return {
    draft,
    setDraft,
    currency,
    setCurrency,
    sellerEnabled,
    setSellerEnabled,
  };
}
